import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";

import { cn } from "@/lib/utils";

// Runtime safety check for Vaul primitives
const isVaulAvailable = Boolean(
  DrawerPrimitive?.Root &&
  DrawerPrimitive?.Portal &&
  DrawerPrimitive?.Overlay &&
  DrawerPrimitive?.Content &&
  DrawerPrimitive?.Title &&
  DrawerPrimitive?.Description
);

// Log warning if Vaul is not available (expected fallback behavior)
if (!isVaulAvailable) {
  console.warn("[Drawer] Vaul primitives are undefined - using fallback mode");
}

// Fallback components when Vaul is not available
const FallbackDrawer = ({ children, open, onOpenChange, ...props }: React.ComponentProps<typeof DrawerPrimitive.Root>) => {
  if (!open) return null;
  return <>{children}</>;
};

const FallbackDrawerPortal = ({ children }: { children: React.ReactNode }) => <>{children}</>;

const FallbackDrawerOverlay = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { onClick?: () => void }
>(({ className, onClick, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "fixed inset-0 z-40 bg-black/80 sm:backdrop-blur-sm",
      className
    )}
    onClick={onClick}
    {...props}
  />
));
FallbackDrawerOverlay.displayName = "FallbackDrawerOverlay";

const FallbackDrawerContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <FallbackDrawerPortal>
    <FallbackDrawerOverlay />
    <div
      ref={ref}
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 mt-24 flex h-auto flex-col rounded-t-[10px] border bg-background",
        "duration-300 ease-out motion-reduce:transition-none",
        className,
      )}
      {...props}
    >
      <div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
      {children}
    </div>
  </FallbackDrawerPortal>
));
FallbackDrawerContent.displayName = "FallbackDrawerContent";

const FallbackDrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)} {...props} />
);

const FallbackDrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("mt-auto flex flex-col gap-2 p-4", className)} {...props} />
);

const FallbackDrawerTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
FallbackDrawerTitle.displayName = "FallbackDrawerTitle";

const FallbackDrawerDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
FallbackDrawerDescription.displayName = "FallbackDrawerDescription";

// Actual Vaul-based components (only created if Vaul is available)
const VaulDrawer = ({ shouldScaleBackground = false, ...props }: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} {...props} />
);

const VaulDrawerTrigger = DrawerPrimitive.Trigger;
const VaulDrawerPortal = DrawerPrimitive.Portal;
const VaulDrawerClose = DrawerPrimitive.Close;

const VaulDrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay 
    ref={ref} 
    className={cn(
      "fixed inset-0 z-40 bg-black/80 sm:backdrop-blur-sm motion-reduce:transition-none",
      className
    )} 
    {...props} 
  />
));
VaulDrawerOverlay.displayName = DrawerPrimitive.Overlay?.displayName ?? "DrawerOverlay";

const VaulDrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <VaulDrawerPortal>
    <VaulDrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 mt-24 flex h-auto flex-col rounded-t-[10px] border bg-background",
        "duration-300 ease-out motion-reduce:transition-none",
        className,
      )}
      {...props}
    >
      <div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
      {children}
    </DrawerPrimitive.Content>
  </VaulDrawerPortal>
));
VaulDrawerContent.displayName = "DrawerContent";

const VaulDrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)} {...props} />
);

const VaulDrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("mt-auto flex flex-col gap-2 p-4", className)} {...props} />
);

const VaulDrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
VaulDrawerTitle.displayName = DrawerPrimitive.Title?.displayName ?? "DrawerTitle";

const VaulDrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
VaulDrawerDescription.displayName = DrawerPrimitive.Description?.displayName ?? "DrawerDescription";

// Export the appropriate components based on Vaul availability
const Drawer = isVaulAvailable ? VaulDrawer : FallbackDrawer;
const DrawerTrigger = isVaulAvailable ? VaulDrawerTrigger : (() => null) as React.FC;
const DrawerPortal = isVaulAvailable ? VaulDrawerPortal : FallbackDrawerPortal;
const DrawerClose = isVaulAvailable ? VaulDrawerClose : (() => null) as React.FC;
const DrawerOverlay = isVaulAvailable ? VaulDrawerOverlay : FallbackDrawerOverlay;
const DrawerContent = isVaulAvailable ? VaulDrawerContent : FallbackDrawerContent;
const DrawerHeader = isVaulAvailable ? VaulDrawerHeader : FallbackDrawerHeader;
const DrawerFooter = isVaulAvailable ? VaulDrawerFooter : FallbackDrawerFooter;
const DrawerTitle = isVaulAvailable ? VaulDrawerTitle : FallbackDrawerTitle;
const DrawerDescription = isVaulAvailable ? VaulDrawerDescription : FallbackDrawerDescription;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
